import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { useLocalizedSettings } from '../../hooks/useLocalizedSettings';
import { useScrollToHash } from '../../hooks/useScrollToHash';
import { localizePath } from '../../utils/i18nPath';
import Logo from '../../assets/images/logo-white.webp';
import { IoLogoFacebook, IoLogoInstagram } from 'react-icons/io';
import { IoChevronForwardOutline } from 'react-icons/io5';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';
import { NAV_LINKS } from './navLinks';
import { HomeLink } from './HomeLink';
import { IS_REACT_SNAP } from '../../utils/isReactSnap';

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Admin link renders client-side only. Both guards are needed:
 * !IS_REACT_SNAP keeps it out of the static HTML, hasMounted keeps it out of
 * the first client render so the two match.
 */
const useHasMounted = () => {
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);
    return hasMounted;
};

/**
 * Shared Footer component — used across public pages.
 *
 * Props:
 *   showAdmin — whether to show the admin link in the bottom bar
 */
export const Footer = ({ showAdmin = false }) => {
    const hasMounted = useHasMounted();
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { settings, s, phoneHref } = useLocalizedSettings();

    // Smooth-scroll handler shared with PageHeader (rAF-based, no race).
    const handleHashClick = useScrollToHash();

    // Build a localized URL for any base href.
    const toLocalizedHref = (href) => localizePath(href, language);


    return (
        <footer className="bg-gray-900 text-gray-300">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                    {/* Column 1: Brand */}
                    <div className="sm:col-span-2 lg:col-span-1">
                        <img className="w-36 sm:w-40 h-auto mb-4" src={Logo} alt="UDent logo" />
                        <p className="text-sm text-gray-400 leading-relaxed mb-6">
                            {t('footer.description')}
                        </p>
                        <div className="flex items-center space-x-4">
                            {settings.facebookUrl && (
                                <a href={settings.facebookUrl} aria-label="Facebook" target="_blank" rel="noopener noreferrer"
                                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold transition-colors">
                                    <IoLogoFacebook size="20px" />
                                </a>
                            )}
                            {settings.instagramUrl && (
                                <a href={settings.instagramUrl} aria-label="Instagram" target="_blank" rel="noopener noreferrer"
                                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold transition-colors">
                                    <IoLogoInstagram size="20px" />
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div>
                        <h4 className="text-white font-bold text-lg mb-4">{t('footer.quickLinks')}</h4>
                        <ul className="space-y-3 mt-2">
                            {NAV_LINKS.map(({ key, href, isRoute }) => {
                                const linkClass = "text-sm text-gray-400 hover:text-gold transition-colors flex items-center space-x-2";
                                if (!isRoute) {
                                    return (
                                        <li key={href}>
                                            <a href={toLocalizedHref(href)} onClick={(event) => handleHashClick(event, href)} className={linkClass}>
                                                <IoChevronForwardOutline className="text-gold text-xs" />
                                                <span>{t(key)}</span>
                                            </a>
                                        </li>
                                    );
                                }
                                const LinkComponent = href === '/' ? HomeLink : Link;
                                return (
                                    <li key={href}>
                                        <LinkComponent to={toLocalizedHref(href)} className={linkClass}>
                                            <IoChevronForwardOutline className="text-gold text-xs" />
                                            <span>{t(key)}</span>
                                        </LinkComponent>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Column 3: Contact */}
                    <div>
                        <h4 className="text-white font-bold text-lg mb-4">{t('footer.contactInfo')}</h4>
                        <ul className="space-y-3 mt-2">
                            <li className="flex items-start space-x-3 text-sm">
                                <FaMapMarkerAlt className="text-gold mt-1 flex-shrink-0" />
                                <span className="text-gray-400">{s('address')}</span>
                            </li>
                            <li className="flex items-center space-x-3 text-sm">
                                <FaPhoneAlt className="text-gold flex-shrink-0" />
                                <a href={phoneHref} className="text-gray-400 hover:text-gold transition-colors">
                                    {settings.phone}
                                </a>
                            </li>
                            <li className="flex items-center space-x-3 text-sm">
                                <FaEnvelope className="text-gold flex-shrink-0" />
                                <a href={`mailto:${settings.email}`} className="text-gray-400 hover:text-gold transition-colors">
                                    {settings.email}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Column 4: Working Hours */}
                    <div>
                        <h4 className="text-white font-bold text-lg mb-4">{t('footer.workingHours')}</h4>
                        <ul className="space-y-3 mt-2 text-sm text-gray-400">
                            <li className="flex items-center space-x-3">
                                <FaClock className="text-gold flex-shrink-0" />
                                <span>{s('hoursWeekdays')}</span>
                            </li>
                            <li className="flex items-center space-x-3">
                                <FaClock className="text-gold flex-shrink-0" />
                                <span>{s('hoursSaturday')}</span>
                            </li>
                            <li className="flex items-center space-x-3">
                                <FaClock className="text-gray-500 flex-shrink-0" />
                                <span>{s('hoursSunday')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between text-sm text-gray-500 gap-3">
                    {/* One string, not five text nodes — see hydration note. */}
                    <p>{`© ${CURRENT_YEAR} UDent. ${t('footer.rights')}.`}</p>
                    {showAdmin && hasMounted && !IS_REACT_SNAP && (
                        <div className="flex items-center space-x-6">
                            <Link
                                to="/admin/login"
                                rel="nofollow"
                                className="hover:text-gold transition-colors"
                            >
                                Admin
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
};

